import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { ChevronDown, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const content = {
  en: {
    title: 'Terms and Conditions',
    lastUpdated: 'Last updated: February 2026',
    sections: [{
      heading: '1. Service Description',
      body: 'CV Go App is a web-based tool that generates tailored, ATS-ready resumes and formal cover letters based on user-provided job descriptions and resumes. The service includes an in-app improvement report and missing keywords analysis. CV Go App does not guarantee employment, interviews, or any specific outcome from using the service.'
    }, {
      heading: '2. Subscription and Payment',
      body: 'Access to CV Go App requires an annual subscription of $9.99 USD, billed as a single payment. The subscription grants 12 months of access from the date of purchase. Prices are subject to change with reasonable notice. All payments are final and non-refundable unless required by applicable law.'
    }, {
      heading: '3. Fair Use Policy',
      body: 'To ensure service reliability and quality for all users, CV Go App limits usage to 5 generations per day. Each generation includes one tailored resume and one cover letter. Attempts to circumvent this limit may result in temporary or permanent suspension of access.'
    }, {
      heading: '4. User Content and Privacy',
      body: 'CV Go App does not store your resume, job descriptions, or generated documents. All content is processed in real-time and delivered to you for download. Once your session ends, your data is not retained on our servers. You are solely responsible for saving and managing your generated documents.'
    }, {
      heading: '5. Acceptable Use',
      body: 'You agree to use CV Go App only for its intended purpose: generating tailored resumes and cover letters for legitimate job applications. You may not use the service to generate misleading, fraudulent, or deceptive content. You are responsible for ensuring the accuracy and truthfulness of all information in your final documents.'
    }, {
      heading: '6. Intellectual Property',
      body: 'The CV Go App platform, including its design, code, algorithms, and branding, is the intellectual property of CV Go App. Generated documents are yours to use freely for personal job applications. You may not resell, redistribute, or commercially exploit the generated output.'
    }, {
      heading: '7. Disclaimers',
      body: 'CV Go App is provided "as is" without warranties of any kind, express or implied. We do not guarantee that the service will improve your chances of getting hired, that ATS systems will rank your resume favorably, or that the service will be uninterrupted or error-free. Results depend on many factors beyond our control, including the quality of your input and employer-specific requirements.'
    }, {
      heading: '8. Limitation of Liability',
      body: 'To the maximum extent permitted by law, CV Go App and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the service. Our total liability shall not exceed the amount you paid for your subscription.'
    }, {
      heading: '9. Modifications',
      body: 'We reserve the right to modify these terms at any time. Changes will be effective upon posting. Continued use of the service after changes constitutes acceptance of the updated terms.'
    }, {
      heading: '10. Governing Law',
      body: 'These terms shall be governed by and construed in accordance with the laws of the United States. Any disputes arising from these terms or the use of CV Go App shall be resolved in the appropriate courts.'
    }]
  },
  es: {
    title: 'Términos y Condiciones',
    lastUpdated: 'Última actualización: febrero 2026',
    sections: [{
      heading: '1. Descripción del Servicio',
      body: 'CV Go App es una herramienta web que genera CVs adaptados y compatibles con ATS, junto con cartas de presentación formales, basándose en las ofertas de empleo y CVs proporcionados por el usuario. El servicio incluye un reporte de mejoras y un análisis de palabras clave faltantes. CV Go App no garantiza empleo, entrevistas ni ningún resultado específico derivado del uso del servicio.'
    }, {
      heading: '2. Suscripción y Pago',
      body: 'El acceso a CV Go App requiere una suscripción anual de $9.99 USD, cobrada como un pago único. La suscripción otorga 12 meses de acceso desde la fecha de compra. Los precios están sujetos a cambios con aviso razonable. Todos los pagos son definitivos y no reembolsables, salvo que la ley aplicable lo requiera.'
    }, {
      heading: '3. Política de Uso Justo',
      body: 'Para garantizar la fiabilidad y calidad del servicio para todos los usuarios, CV Go App limita el uso a 5 generaciones por día. Cada generación incluye un CV adaptado y una carta de presentación. Los intentos de eludir este límite pueden resultar en la suspensión temporal o permanente del acceso.'
    }, {
      heading: '4. Contenido del Usuario y Privacidad',
      body: 'CV Go App no almacena tu CV, ofertas de empleo ni documentos generados. Todo el contenido se procesa en tiempo real y se entrega para su descarga. Una vez que tu sesión finaliza, tus datos no se conservan en nuestros servidores. Eres el único responsable de guardar y gestionar tus documentos generados.'
    }, {
      heading: '5. Uso Aceptable',
      body: 'Aceptas usar CV Go App únicamente para su propósito previsto: generar CVs y cartas de presentación adaptados para postulaciones legítimas. No puedes usar el servicio para generar contenido engañoso, fraudulento o falso. Eres responsable de garantizar la veracidad y exactitud de toda la información en tus documentos finales.'
    }, {
      heading: '6. Propiedad Intelectual',
      body: 'La plataforma CV Go App, incluyendo su diseño, código, algoritmos y marca, es propiedad intelectual de CV Go App. Los documentos generados son de tu uso libre para postulaciones personales. No puedes revender, redistribuir ni explotar comercialmente los documentos generados.'
    }, {
      heading: '7. Descargo de Responsabilidad',
      body: 'CV Go App se proporciona "tal cual" sin garantías de ningún tipo, expresas o implícitas. No garantizamos que el servicio mejore tus posibilidades de ser contratado, que los sistemas ATS clasifiquen tu CV favorablemente, ni que el servicio sea ininterrumpido o libre de errores. Los resultados dependen de muchos factores fuera de nuestro control, incluyendo la calidad de tu input y los requisitos específicos del empleador.'
    }, {
      heading: '8. Limitación de Responsabilidad',
      body: 'En la máxima medida permitida por la ley, CV Go App y sus operadores no serán responsables de daños indirectos, incidentales, especiales, consecuentes o punitivos derivados del uso del servicio. Nuestra responsabilidad total no excederá el monto que pagaste por tu suscripción.'
    }, {
      heading: '9. Modificaciones',
      body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios serán efectivos una vez publicados. El uso continuado del servicio después de los cambios constituye la aceptación de los términos actualizados.'
    }, {
      heading: '10. Ley Aplicable',
      body: 'Estos términos se regirán e interpretarán de acuerdo con las leyes de los Estados Unidos. Cualquier disputa derivada de estos términos o del uso de CV Go App se resolverá en los tribunales correspondientes.'
    }]
  }
};
interface TermsAndConditionsProps {
  isOpen: boolean;
  onClose: () => void;
}
export function TermsAndConditions({
  isOpen,
  onClose
}: TermsAndConditionsProps) {
  const {
    language
  } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const t = content[language];
  return <AnimatePresence data-id="element-129">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={onClose} data-id="element-130" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="fixed inset-4 sm:inset-8 md:inset-y-12 md:inset-x-auto md:max-w-3xl md:mx-auto z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-131">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0" data-id="element-132">
              <div data-id="element-133">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-134">{t.title}</h2>
                <p className="text-xs text-gray-400 mt-0.5" data-id="element-135">{t.lastUpdated}</p>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-136">
                <X className="w-5 h-5" data-id="element-137" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-6 py-4" data-id="element-138">
              <div className="space-y-2" data-id="element-139">
                {t.sections.map((section, i) => {
              const isSectionOpen = openIndex === i;
              return <div key={i} className="border border-gray-100 rounded-lg overflow-hidden bg-gray-50/50" data-id="element-140">
                      <button onClick={() => setOpenIndex(isSectionOpen ? null : i)} className="w-full px-5 py-4 flex items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded-lg" aria-expanded={isSectionOpen} data-id="element-141">
                        <span className="font-semibold text-sm text-gray-800" data-id="element-142">
                          {section.heading}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isSectionOpen ? 'rotate-180' : ''}`} data-id="element-143" />
                      </button>

                      <AnimatePresence data-id="element-144">
                        {isSectionOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-145">
                            <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3" data-id="element-146">
                              {section.body}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
            })}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}